import React, { useRef } from 'react';

import { getTranslation, keys } from '../../../../../../../Resources/Translations';
import { Loader } from '../../../../../../Components/Loader/Loader';

interface AddToCartQuantityPickerProps {
    value: number;
    setValue: (value: number) => void;
    quantityOrdered: number;
    minQuantity: number;
    maxQuantity: number;
    isLoading: boolean;
    modifyQuantity: (quantity: number) => void;
}

export const AddToCartQuantityPicker = ({
    value,
    setValue,
    quantityOrdered,
    minQuantity,
    maxQuantity,
    isLoading,
    modifyQuantity
}: AddToCartQuantityPickerProps) => {
    const setQuantityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const shouldIncreaseBeDisabled = (): boolean => {
        return value >= maxQuantity;
    };

    const shouldDecreaseBeDisabled = (): boolean => {
        return value <= minQuantity;
    };

    const increaseQuantity = () => {
        if (shouldIncreaseBeDisabled()) return;
        setQuantityWithTimeout(value + 1);
    };

    const decreaseQuantity = () => {
        if (shouldDecreaseBeDisabled()) return;
        if (value > 1) {
            setQuantityWithTimeout(value - 1);
        }
    };

    const setQuantityWithTimeout = (value: number) => {
        setValue(value);

        clearSetQuantityTimeout();
        setQuantityTimeoutRef.current = setTimeout(() => {
            modifyQuantity(value - quantityOrdered);
        }, 500);
    };

    const clearSetQuantityTimeout = () => {
        setQuantityTimeoutRef.current && clearTimeout(setQuantityTimeoutRef.current);
        setQuantityTimeoutRef.current = null;
    };

    return (
        <div className="AddToCartQuantityPicker">
            <Loader isLoading={isLoading} className="AddToCartQuantityPicker__Loader" />
            <button
                className="AddToCartQuantityPicker__Button AddToCartQuantityPicker__Button--Remove"
                onClick={decreaseQuantity}
                aria-disabled={shouldDecreaseBeDisabled()}
                type="button"
            >
                <span className="visuallyHidden">{getTranslation(keys['cartSummary.increaseQuantity'])}</span>
            </button>
            <span className="AddToCartQuantityPicker__Quantity">{value}</span>
            <button
                className="AddToCartQuantityPicker__Button AddToCartQuantityPicker__Button--Add"
                onClick={increaseQuantity}
                aria-disabled={shouldIncreaseBeDisabled()}
                type="button"
            >
                <span className="visuallyHidden">{getTranslation(keys['cartSummary.decreaseQuantity'])}</span>
            </button>
        </div>
    );
};
